import React, { useEffect, useRef } from 'react';
import {
	ErrorAlertIcon,
	CheckCircleIcon,
	InfoAlertIcon,
	WarningAlertIcon,
} from '../../Icons/AlertIcons/AlertIcons';
import './FlyoverNotification.css';

// ──────────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────────

export const enum FlyoverType {
	Error = 'error',
	Success = 'success',
	Warning = 'warning',
	Info = 'info',
}

export interface FlyoverNotificationProps {
	/** Variant that controls colour scheme and icon */
	type: FlyoverType;
	/** The message to display */
	message: string;
	/** Whether the flyover is currently visible */
	visible: boolean;
	/** Called when the flyover should be dismissed */
	onDismiss: () => void;
	/** Auto-dismiss after this many milliseconds. 0 = never auto-dismiss. Default: 1000 */
	autoDismissMs?: number;
}

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const typeConfig: Record<FlyoverType, { icon: React.ReactNode; modifierClass: string }> =
	{
		[FlyoverType.Error]: {
			icon: <ErrorAlertIcon width={16} height={16} />,
			modifierClass: 'flyover--error',
		},
		[FlyoverType.Success]: {
			icon: <CheckCircleIcon width={16} height={16} />,
			modifierClass: 'flyover--success',
		},
		[FlyoverType.Warning]: {
			icon: <WarningAlertIcon width={16} height={16} />,
			modifierClass: 'flyover--warning',
		},
		[FlyoverType.Info]: {
			icon: <InfoAlertIcon width={16} height={16} />,
			modifierClass: 'flyover--info',
		},
	};

// ──────────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────────

export const FlyoverNotification: React.FC<FlyoverNotificationProps> = ({
	type,
	message,
	visible,
	onDismiss,
	autoDismissMs = 1000,
}) => {
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (visible && autoDismissMs > 0) {
			timerRef.current = setTimeout(() => {
				onDismiss();
			}, autoDismissMs);
		}

		return () => {
			if (timerRef.current !== null) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [visible, autoDismissMs, onDismiss]);

	if (!visible) {
		return null;
	}

	const { icon, modifierClass } = typeConfig[type];

	return (
		<div
			className={`flyover ${modifierClass}`}
			role="alert"
			aria-live="assertive"
			aria-atomic="true"
			data-testid="flyover-notification"
		>
			<span className="flyover__icon" aria-hidden="true">
				{icon}
			</span>
			<span className="flyover__message">{message}</span>
		</div>
	);
};
